/**
 * Recursive IR layout pass.
 *
 * Step 4 of the IR build (see docs/IR_RULE_MAPPING.md §7 step 4).
 *
 * INPUT:  { start, sequence, end } from flatToIR()
 * OUTPUT: Same wrapper, decorated so that
 *         - every IR node has `bbox: {x, y, w, h}` in its PARENT's local space,
 *         - container nodes also have `localTrunkX` (the trunk centerline in
 *           the container's own local space) and `subtreeWidth` (the full
 *           horizontal footprint of the container including branches/cases),
 *         - the wrapper has `bbox` covering Start + sequence + End.
 *
 * DESIGN
 * ──────
 *  1. **Bottom-up width, top-down position.** A single recursive call per
 *     node does both: children are laid out first (yielding their widths
 *     and heights), then the container sizes itself and positions the
 *     children into its local space.
 *
 *  2. **Coordinates are LOCAL to the parent.** A child inside a Switch
 *     Case has `bbox.x/y` relative to the Case; the Case has `bbox.x/y`
 *     relative to the Switch; the Switch relative to the root wrapper.
 *     This makes `irToFlat` (step 5) a straightforward absolute-coordinate
 *     accumulation.
 *
 *  3. **Trunk-centered stacking.** In a vertical sequence (top-level
 *     trunk, Case.children, Branch.children, DoWhile.body), each child's
 *     bbox is centered so that the child's `localTrunkX` aligns with the
 *     enclosing container's trunk centerline. That keeps the spine
 *     visually vertical regardless of any children's horizontal splay.
 *
 *  4. **Symmetric splay.** Switch cases and Fork branches are spread
 *     horizontally with a fixed gap, ordered left-to-right as they appear
 *     in the IR. No children → zero-width case; the renderer will later
 *     emit a dashed ghost edge (step 5 concern, not this file's).
 *
 *  5. **Dimensions come from IR_DIMS.** Every head size / sentinel size is
 *     stamped by the constructors in types.js and re-asserted here via
 *     IR_DIMS — never hard-coded. Gap constants live in LAYOUT_DIMS below
 *     (step 4's own concern).
 *
 * NOT IN SCOPE for this step:
 *   - Port placement (step 6).
 *   - Height feedback from the DOM (step 7).
 *   - SWITCH convergence pill placement (step 5+).
 */

import { IR_KIND } from './kinds';
import { IR_DIMS } from './types';

// ───────────────────────────────────────────────────────────────────
//  Layout constants (gaps between stacked / splayed nodes)
// ───────────────────────────────────────────────────────────────────

export const LAYOUT_DIMS = Object.freeze({
  /** Vertical gap between consecutive nodes in a sequence / body. */
  V_GAP: 80,
  /** Horizontal gap between adjacent SWITCH cases. */
  CASE_GAP: 100,
  /** Horizontal gap between adjacent FORK branches. */
  BRANCH_GAP: 100,
  /** Vertical gap between branches and their paired JOIN. */
  JOIN_GAP: 80,
  /** Padding between the DoWhile body bbox and the surrounding frame. */
  LOOP_FRAME_PAD: 40,
  /** Default Leaf height before DOM measurement (Rule S-G.a bucket 100). */
  LEAF_H_DEFAULT: 100,
});

// ───────────────────────────────────────────────────────────────────
//  Public entry point
// ───────────────────────────────────────────────────────────────────

/**
 * Decorate an IR wrapper with bboxes. Input wrapper is NOT mutated; the
 * returned wrapper has laid-out copies of every node.
 *
 * @param {{ start: object, sequence: object[], end: object|null }} wrapper
 * @returns {{ start: object, sequence: object[], end: object|null, bbox: {x,y,w,h} }}
 */
export function layoutIR(wrapper) {
  if (!wrapper || !wrapper.start || !Array.isArray(wrapper.sequence)) {
    throw new Error('layoutIR: expected { start, sequence, end }');
  }

  // 1. Lay out each piece as its own mini-tree (relative to itself).
  const laidStart = layoutNode(wrapper.start);
  const laidSeq   = layoutSequenceArray(wrapper.sequence);
  const laidEnd   = wrapper.end ? layoutNode(wrapper.end) : null;

  // 2. Determine the wrapper's overall width — the widest of the three
  //    vertical bands. The trunk centerline runs down the middle.
  const contentW = Math.max(
    laidStart.bbox.w,
    laidSeq.subtreeWidth,
    laidEnd ? laidEnd.bbox.w : 0,
  );
  const trunkX = contentW / 2;

  // 3. Stack vertically: Start, sequence, End — each centered on trunkX.
  let y = 0;

  laidStart.bbox.x = trunkX - laidStart.localTrunkX;
  laidStart.bbox.y = y;
  y += laidStart.bbox.h + LAYOUT_DIMS.V_GAP;

  // Shift sequence children into the wrapper's coordinate space:
  //   - horizontally, align seq's trunk with trunkX
  //   - vertically, offset by current y cursor
  const seqOffsetX = trunkX - laidSeq.localTrunkX;
  for (const child of laidSeq.children) {
    child.bbox.x += seqOffsetX;
    child.bbox.y += y;
  }
  y += laidSeq.height;
  if (laidSeq.children.length > 0) y += LAYOUT_DIMS.V_GAP;

  if (laidEnd) {
    laidEnd.bbox.x = trunkX - laidEnd.localTrunkX;
    laidEnd.bbox.y = y;
    y += laidEnd.bbox.h;
  }

  return {
    start: laidStart,
    sequence: laidSeq.children,
    end: laidEnd,
    bbox: { x: 0, y: 0, w: contentW, h: y },
  };
}

// ───────────────────────────────────────────────────────────────────
//  Per-variant dispatch
// ───────────────────────────────────────────────────────────────────

function layoutNode(node) {
  switch (node.kind) {
    case IR_KIND.LEAF:          return layoutLeaf(node);
    case IR_KIND.TERMINATE:     return layoutTerminate(node);
    case IR_KIND.START:
    case IR_KIND.END:           return layoutSentinel(node);
    case IR_KIND.SUB_WORKFLOW:  return layoutSubWorkflow(node);
    case IR_KIND.SWITCH:        return layoutSwitch(node);
    case IR_KIND.FORK:
    case IR_KIND.DYNAMIC_FORK:  return layoutFork(node);
    case IR_KIND.DO_WHILE:      return layoutDoWhile(node);
    default:
      throw new Error(`layoutIR: unknown IR kind '${node.kind}' on id '${node.id}'`);
  }
}

// ───────────────────────────────────────────────────────────────────
//  Scalars (no children, bbox = head dims)
// ───────────────────────────────────────────────────────────────────

function layoutLeaf(n) {
  const h = typeof n.measuredH === 'number' ? n.measuredH : LAYOUT_DIMS.LEAF_H_DEFAULT;
  return {
    ...n,
    bbox: { x: 0, y: 0, w: n.w, h },
    subtreeWidth: n.w,
    localTrunkX: n.w / 2,
  };
}

function layoutTerminate(n) {
  return {
    ...n,
    bbox: { x: 0, y: 0, w: n.w, h: n.h },
    subtreeWidth: n.w,
    localTrunkX: n.w / 2,
  };
}

function layoutSentinel(n) {
  return {
    ...n,
    bbox: { x: 0, y: 0, w: n.w, h: n.h },
    subtreeWidth: n.w,
    localTrunkX: n.w / 2,
  };
}

function layoutSubWorkflow(n) {
  // Embedded expansion is a later-step concern; design-time SubWorkflow is
  // a scalar-sized head.
  return {
    ...n,
    bbox: { x: 0, y: 0, w: n.headW, h: n.headH },
    subtreeWidth: n.headW,
    localTrunkX: n.headW / 2,
  };
}

// ───────────────────────────────────────────────────────────────────
//  Sequence (vertical stack of IR nodes)
// ───────────────────────────────────────────────────────────────────

/**
 * Lay out an array of IR nodes as a vertical sequence. Each child is
 * centered so its own trunk aligns with the sequence's trunk centerline.
 *
 * Returns an intermediate shape (NOT itself an IR node):
 *   {
 *     children:     laid-out IR nodes with bbox set in the sequence's local space,
 *     subtreeWidth: max child subtreeWidth (sequence width),
 *     height:       total stacked height,
 *     localTrunkX:  subtreeWidth / 2 (the sequence trunk centerline),
 *   }
 */
function layoutSequenceArray(nodes) {
  if (nodes.length === 0) {
    return { children: [], subtreeWidth: 0, height: 0, localTrunkX: 0 };
  }

  const laid = nodes.map(layoutNode);
  const subtreeWidth = Math.max(...laid.map(c => c.subtreeWidth));
  const localTrunkX = subtreeWidth / 2;

  let y = 0;
  for (let i = 0; i < laid.length; i++) {
    const c = laid[i];
    // Align child trunk with sequence trunk
    c.bbox.x = localTrunkX - c.localTrunkX;
    c.bbox.y = y;
    y += c.bbox.h;
    if (i < laid.length - 1) y += LAYOUT_DIMS.V_GAP;
  }

  return { children: laid, subtreeWidth, height: y, localTrunkX };
}

// ───────────────────────────────────────────────────────────────────
//  Switch
// ───────────────────────────────────────────────────────────────────

/**
 * Switch layout:
 *
 *     head(450×200) centered on trunkX
 *           │
 *       ┌───┼───────────┐   ← splay cases horizontally
 *     case0 case1 ... default
 *       │     │          │
 *      body  body       body
 *
 * subtreeWidth = max(headW, total splayed cases)
 * subtreeHeight = headH + V_GAP + max(case body heights)
 */
function layoutSwitch(n) {
  const allCases = [...n.cases];
  if (n.defaultCase) allCases.push(n.defaultCase);

  // Layout each case's children sequence.
  const caseLayouts = allCases.map(c => ({
    caseObj: c,
    seq: layoutSequenceArray(c.children),
  }));

  // A case's footprint is at least a leaf-width wide (so empty cases still
  // take visual space for a ghost edge in step 5).
  const caseFootprints = caseLayouts.map(cl => Math.max(cl.seq.subtreeWidth, IR_DIMS.LEAF_W));
  const nCases = caseLayouts.length;
  const totalCasesW = nCases === 0
    ? 0
    : caseFootprints.reduce((s, w) => s + w, 0) + (nCases - 1) * LAYOUT_DIMS.CASE_GAP;

  const headW = IR_DIMS.SWITCH_HEAD_W;
  const headH = IR_DIMS.SWITCH_HEAD_H;
  const subtreeWidth = Math.max(headW, totalCasesW);
  const localTrunkX = subtreeWidth / 2;

  // Place cases below the head, splayed symmetrically around localTrunkX.
  const caseBandY = headH + LAYOUT_DIMS.V_GAP;
  let cursorX = localTrunkX - totalCasesW / 2;
  const laidAllCases = [];
  let casesMaxH = 0;

  for (let i = 0; i < caseLayouts.length; i++) {
    const { caseObj, seq } = caseLayouts[i];
    const bw = caseFootprints[i];
    const caseOriginX = cursorX;
    const caseLocalTrunk = bw / 2;

    // Shift children so that within the case's bbox, the trunk sits at bw/2.
    for (const child of seq.children) {
      child.bbox.x += (caseLocalTrunk - seq.localTrunkX);
    }

    laidAllCases.push({
      ...caseObj,
      children: seq.children,
      bbox: { x: caseOriginX, y: caseBandY, w: bw, h: seq.height },
      subtreeWidth: bw,
      localTrunkX: caseLocalTrunk,
    });
    casesMaxH = Math.max(casesMaxH, seq.height);
    cursorX += bw + LAYOUT_DIMS.CASE_GAP;
  }

  const subtreeHeight = nCases === 0 ? headH : (headH + LAYOUT_DIMS.V_GAP + casesMaxH);

  // Split back into cases vs defaultCase.
  const laidCases = n.defaultCase ? laidAllCases.slice(0, -1) : laidAllCases;
  const laidDefault = n.defaultCase ? laidAllCases[laidAllCases.length - 1] : null;

  return {
    ...n,
    cases: laidCases,
    defaultCase: laidDefault,
    bbox: { x: 0, y: 0, w: subtreeWidth, h: subtreeHeight },
    subtreeWidth,
    localTrunkX,
    headBBox: { x: localTrunkX - headW / 2, y: 0, w: headW, h: headH },
  };
}

// ───────────────────────────────────────────────────────────────────
//  Fork / DynamicFork
// ───────────────────────────────────────────────────────────────────

/**
 * Fork layout:
 *
 *     head(350×120)
 *         │
 *     splayed branches
 *         │
 *     join(350×120) — absorbed into parent Fork node (not a standalone IR)
 */
function layoutFork(n) {
  // DynamicFork's resolvedBranches is null at design time — treat as an
  // empty-branches Fork for layout purposes (the head itself is the whole
  // footprint, with the dynamic-resolve badge at the bottom).
  const rawBranches = n.kind === IR_KIND.DYNAMIC_FORK
    ? (n.resolvedBranches || [])
    : n.branches;

  const branchLayouts = rawBranches.map(b => ({
    branchObj: b,
    seq: layoutSequenceArray(b.children),
  }));

  const branchFootprints = branchLayouts.map(bl => Math.max(bl.seq.subtreeWidth, IR_DIMS.LEAF_W));
  const nBranches = branchLayouts.length;
  const totalBranchesW = nBranches === 0
    ? 0
    : branchFootprints.reduce((s, w) => s + w, 0) + (nBranches - 1) * LAYOUT_DIMS.BRANCH_GAP;

  const headW = IR_DIMS.FORK_HEAD_W;
  const headH = IR_DIMS.FORK_HEAD_H;
  const joinW = IR_DIMS.FORK_HEAD_W;
  const joinH = IR_DIMS.FORK_HEAD_H;

  const subtreeWidth = Math.max(headW, totalBranchesW);
  const localTrunkX = subtreeWidth / 2;

  const branchBandY = headH + LAYOUT_DIMS.V_GAP;
  let cursorX = localTrunkX - totalBranchesW / 2;
  const laidBranches = [];
  let branchesMaxH = 0;

  for (let i = 0; i < branchLayouts.length; i++) {
    const { branchObj, seq } = branchLayouts[i];
    const bw = branchFootprints[i];
    const originX = cursorX;
    const branchTrunkX = bw / 2;

    for (const child of seq.children) {
      child.bbox.x += (branchTrunkX - seq.localTrunkX);
    }

    laidBranches.push({
      ...branchObj,
      children: seq.children,
      bbox: { x: originX, y: branchBandY, w: bw, h: seq.height },
      subtreeWidth: bw,
      localTrunkX: branchTrunkX,
    });
    branchesMaxH = Math.max(branchesMaxH, seq.height);
    cursorX += bw + LAYOUT_DIMS.BRANCH_GAP;
  }

  // Join: centered on trunk, below the branches.
  const joinY = nBranches === 0
    ? headH + LAYOUT_DIMS.JOIN_GAP
    : branchBandY + branchesMaxH + LAYOUT_DIMS.JOIN_GAP;
  const laidJoin = n.join
    ? {
        ...n.join,
        bbox: { x: localTrunkX - joinW / 2, y: joinY, w: joinW, h: joinH },
        localTrunkX: joinW / 2,
      }
    : null;

  const subtreeHeight = joinY + joinH;

  const result = {
    ...n,
    bbox: { x: 0, y: 0, w: subtreeWidth, h: subtreeHeight },
    subtreeWidth,
    localTrunkX,
    headBBox: { x: localTrunkX - headW / 2, y: 0, w: headW, h: headH },
    join: laidJoin,
  };

  if (n.kind === IR_KIND.DYNAMIC_FORK) {
    result.resolvedBranches = n.resolvedBranches ? laidBranches : null;
  } else {
    result.branches = laidBranches;
  }
  return result;
}

// ───────────────────────────────────────────────────────────────────
//  DoWhile
// ───────────────────────────────────────────────────────────────────

/**
 * DoWhile layout:
 *
 *     head(350×130)
 *         │
 *     ┌─────────────┐     ← frame around body
 *     │   body[0]   │
 *     │   body[1]   │
 *     │     ...     │
 *     └─────────────┘
 *
 * subtreeWidth  = max(headW, bodyWidth + 2*LOOP_FRAME_PAD)
 * subtreeHeight = headH + V_GAP + bodyHeight + 2*LOOP_FRAME_PAD
 */
function layoutDoWhile(n) {
  const bodySeq = layoutSequenceArray(n.body);
  const headW = IR_DIMS.DOWHILE_HEAD_W;
  const headH = IR_DIMS.DOWHILE_HEAD_H;
  const pad = LAYOUT_DIMS.LOOP_FRAME_PAD;

  const frameInnerW = Math.max(bodySeq.subtreeWidth, headW);
  const frameW = frameInnerW + 2 * pad;
  const frameH = bodySeq.height + 2 * pad;

  const subtreeWidth = Math.max(headW, frameW);
  const localTrunkX = subtreeWidth / 2;

  const frameX = localTrunkX - frameW / 2;
  const frameY = headH + LAYOUT_DIMS.V_GAP;

  // Shift body children from their seq-local x to frame-local x, then to
  // subtree-local x.
  const bodyTrunkX = frameInnerW / 2;
  for (const child of bodySeq.children) {
    child.bbox.x += (bodyTrunkX - bodySeq.localTrunkX);  // center inside frame inner
    child.bbox.x += pad;                                  // add frame padding
    child.bbox.x += frameX;                               // translate to subtree-local
    child.bbox.y += frameY + pad;
  }

  const subtreeHeight = frameY + frameH;

  return {
    ...n,
    body: bodySeq.children,
    bbox: { x: 0, y: 0, w: subtreeWidth, h: subtreeHeight },
    subtreeWidth,
    localTrunkX,
    headBBox: { x: localTrunkX - headW / 2, y: 0, w: headW, h: headH },
    frameBBox: { x: frameX, y: frameY, w: frameW, h: frameH },
  };
}

// ───────────────────────────────────────────────────────────────────
//  Runtime validator — assertValidLayout(wrapper)
// ───────────────────────────────────────────────────────────────────

/**
 * Walk a laid-out wrapper and throw on the first invariant violation:
 *   - every node has bbox with w>0, h>0 and finite x, y
 *   - container bbox fully contains its children bboxes (in local coords)
 *   - within a sequence/case/branch/body, children bboxes don't vertically
 *     overlap each other
 *
 * @param {{ start, sequence, end, bbox }} laid
 * @throws {Error} on any violation
 */
export function assertValidLayout(laid) {
  if (!laid || !laid.bbox) throw new Error('assertValidLayout: missing wrapper.bbox');
  assertBBoxShape('wrapper', laid.bbox);
  assertBBoxShape('wrapper.start', laid.start.bbox);
  if (laid.end) assertBBoxShape('wrapper.end', laid.end.bbox);
  assertBBoxInside('wrapper.start', laid.start.bbox, laid.bbox);
  if (laid.end) assertBBoxInside('wrapper.end', laid.end.bbox, laid.bbox);

  laid.sequence.forEach((n, i) => assertValidLaidNode(n, `wrapper.sequence[${i}]`, laid.bbox));

  // Vertical non-overlap across wrapper band: start → sequence → end.
  const bands = [laid.start.bbox, ...laid.sequence.map(n => n.bbox)];
  if (laid.end) bands.push(laid.end.bbox);
  for (let i = 1; i < bands.length; i++) {
    const prev = bands[i - 1];
    const curr = bands[i];
    if (curr.y < prev.y + prev.h) {
      throw new Error(`wrapper band[${i}] vertically overlaps band[${i - 1}]: prev y+h=${prev.y + prev.h}, curr y=${curr.y}`);
    }
  }
}

function assertValidLaidNode(node, pathLabel, parentBBox) {
  if (!node || !node.bbox) throw new Error(`${pathLabel}: missing bbox on ${node && node.kind}`);
  assertBBoxShape(pathLabel, node.bbox);
  assertBBoxInside(pathLabel, node.bbox, parentBBox);

  // Recurse into children, translating their bboxes into the node's local
  // frame but validating they fit inside the node's own bbox (w, h).
  const localBBox = { x: 0, y: 0, w: node.bbox.w, h: node.bbox.h };

  switch (node.kind) {
    case IR_KIND.SWITCH: {
      for (const c of node.cases) assertValidCaseOrBranch(c, `${pathLabel}.case[${c.name}]`, localBBox);
      if (node.defaultCase) assertValidCaseOrBranch(node.defaultCase, `${pathLabel}.default`, localBBox);
      break;
    }
    case IR_KIND.FORK: {
      for (const b of node.branches) assertValidCaseOrBranch(b, `${pathLabel}.branch[${b.name}]`, localBBox);
      if (node.join && node.join.bbox) {
        assertBBoxShape(`${pathLabel}.join`, node.join.bbox);
        assertBBoxInside(`${pathLabel}.join`, node.join.bbox, localBBox);
      }
      break;
    }
    case IR_KIND.DYNAMIC_FORK: {
      if (node.resolvedBranches) {
        for (const b of node.resolvedBranches) assertValidCaseOrBranch(b, `${pathLabel}.resolvedBranch[${b.name}]`, localBBox);
      }
      if (node.join && node.join.bbox) {
        assertBBoxShape(`${pathLabel}.join`, node.join.bbox);
        assertBBoxInside(`${pathLabel}.join`, node.join.bbox, localBBox);
      }
      break;
    }
    case IR_KIND.DO_WHILE: {
      // Body children live inside the frame bbox (which is inside the node's bbox).
      for (let i = 0; i < node.body.length; i++) {
        assertValidLaidNode(node.body[i], `${pathLabel}.body[${i}]`, localBBox);
      }
      break;
    }
    default:
      // Leaves, sentinels, terminate, sub-workflow — no children to check.
      break;
  }
}

function assertValidCaseOrBranch(cb, pathLabel, parentBBox) {
  if (!cb.bbox) throw new Error(`${pathLabel}: missing bbox`);
  assertBBoxShape(pathLabel, cb.bbox);
  assertBBoxInside(pathLabel, cb.bbox, parentBBox);
  const localBBox = { x: 0, y: 0, w: cb.bbox.w, h: cb.bbox.h };
  cb.children.forEach((n, i) => assertValidLaidNode(n, `${pathLabel}.children[${i}]`, localBBox));
}

function assertBBoxShape(pathLabel, bbox) {
  if (!bbox) throw new Error(`${pathLabel}.bbox is missing`);
  for (const f of ['x', 'y', 'w', 'h']) {
    if (typeof bbox[f] !== 'number' || !Number.isFinite(bbox[f])) {
      throw new Error(`${pathLabel}.bbox.${f} must be finite number (got ${bbox[f]})`);
    }
  }
  if (bbox.w <= 0) throw new Error(`${pathLabel}.bbox.w must be > 0 (got ${bbox.w})`);
  if (bbox.h <= 0) throw new Error(`${pathLabel}.bbox.h must be > 0 (got ${bbox.h})`);
}

function assertBBoxInside(pathLabel, bbox, parentBBox) {
  // A child's bbox must fit within the parent's local coordinate space,
  // i.e. 0 ≤ x and x + w ≤ parent.w (same for y/h).
  const slack = 0.5; // float tolerance
  if (bbox.x < -slack) {
    throw new Error(`${pathLabel}.bbox.x must be ≥ 0 (got ${bbox.x})`);
  }
  if (bbox.y < -slack) {
    throw new Error(`${pathLabel}.bbox.y must be ≥ 0 (got ${bbox.y})`);
  }
  if (bbox.x + bbox.w > parentBBox.w + slack) {
    throw new Error(`${pathLabel}: bbox.x+w (${bbox.x + bbox.w}) exceeds parent.w (${parentBBox.w})`);
  }
  if (bbox.y + bbox.h > parentBBox.h + slack) {
    throw new Error(`${pathLabel}: bbox.y+h (${bbox.y + bbox.h}) exceeds parent.h (${parentBBox.h})`);
  }
}
